<template>
  <div :class="$style.reports">
    <reports-title
      ref="reportsTitle"
      :title="pageTitle"
    />
    <reports-toolbar
      ref="reportsToolbar"
      :toolbar-disabled="isDisabledExport"
      :report-title="pageTitle"
      @onClickExport="onExport"
    />
    <skeleton-wrapper
      v-if="isShowSkeleton"
      id="skeletonWrapper"
      :style="{ height: contentHeight }"
    >
      <template #body>
        <div :class="[$style.chart_wrapper, $style.chart_wrapper_pie]">
          <doughnut-chart
            id="projectStatuses"
            :class="'report_export_id'"
            data-chart-type="pie"
            :items="[]"
            :static="true"
            :tooltip-title="locale(`chart_projects_by_statuses_tooltip_title`)"
            :stub-description="''"
            :config="chartViewConfig"
          />
        </div>
      </template>
    </skeleton-wrapper>
    <div
      v-else
      :class="[$style.chart_wrapper, $style.chart_wrapper_pie]"
    >
      <doughnut-chart
        id="projectStatuses"
        :class="'report_export_id'"
        data-chart-type="pie"
        :items="items"
        :static="isStub"
        :tooltip-title="locale(`chart_projects_by_statuses_tooltip_title`)"
        :stub-description="locale(`report_page_no_filtered_data`)"
        :config="chartViewConfig"
        :with-delay="false"
        @onCheckSelected="toggleLegend"
      />
    </div>
  </div>
</template>

<script>
import pricingHelper from '../../../helpers/pricingHelper';
import rights from '../../../components/rights/index';
import reportsTitle from '../include/title.vue';
import reportsToolbar from '../include/toolbar.vue';
import constants from '$$helpers/constants';
import multiViewsProjects from '../../../models/multiViewsProjects';
import DoughnutChart from '$$vueCmp/charts/DoughnutChart.vue';
import reportsConstants from '../include/constants';
import projectsModel from '../../../models/projects';
import skeletonWrapper from '../../common/VueComponents/skeletons/skeletonWrapper.vue';
import Export from '../include/export/export';

const stubData = [
  {
    id: 1,
    color: '#999999',
    name: '-',
    value: 33,
    disable: true,
  },
  {
    id: 2,
    color: '#E9E9E9',
    name: '-',
    value: 22,
    disable: true,
  },
  {
    id: 3,
    color: '#CCCCCC',
    name: '-',
    value: 22,
    disable: true,
  },
  {
    id: 4,
    color: '#E9E9E9',
    name: '-',
    value: 22,
    disable: true,
  },
];

export default {
  components: {
    DoughnutChart,
    reportsTitle,
    reportsToolbar,
    skeletonWrapper,
  },
  props: {
    projects: {
      type: Array,
      required: false,
    },
  },
  data() {
    return {
      type: null,
      locale: __,
      projectsByStatusesData: {
        items: [],
      },
      projectIds: [],
      multiviewProjectIds: [],
      chartViewConfig: {
        large: true,
        labelPosition: 'bottom',
        labelStyle: 'row',
        total: {
          label: __('dashboard_total_projects'),
          show: true,
        },
        legendStyles: {
          fontSize: '16px',
          boxWidth: '35px',
          boxHeight: '20px',
        },
      },
      isStub: true,
      isShowSkeleton: true,
      disabledLegends: false,
      contentHeight: '0px',
    };
  },
  computed: {
    currentProjectIds() {
      const projectIds = [this.projectIds, this.multiviewProjectIds].find(arr => arr.length) || [];

      if (!projectIds.length) {
        return projectsModel.getAllProjects().map(project => project.gantt_id);
      }

      return projectIds;
    },
    pageTitle() {
      return reportsConstants.TITLE.progress.status;
    },
    currentFilter() {
      return this.$store.getters['filter/currentFilter'];
    },
    items() {
      if (this.projectsByStatusesData.items.length) {
        return this.projectsByStatusesData.items.filter(item => !!item.value);
      }

      return stubData;
    },
    accessToReports() {
      return pricingHelper.checkPricingAccess('reports');
    },
    tasks() {
      return this.$store.getters['tasksModel/getTasksByGanttIds'](this.currentProjectIds)
        .filter(task => task.type === constants.TASK_TYPES.task || task.type === constants.TASK_TYPES.milestone);
    },
    milestones() {
      return this.tasks.filter(task => task.type === constants.TASK_TYPES.milestone);
    },
    isOwner() {
      return rights.account.isOwner();
    },
    isDisabledExport() {
      return this.isStub || this.disabledLegends;
    },
    sourceType() {
      if (this.multiviewProjectIds.length) {
        return 'multiview';
      }

      return 'projects';
    },
  },
  watch: {
    'currentFilter.properties.gantt_id': function (projectIds) {
      if (projectIds?.length) {
        this.projectIds = [...projectIds];
      } else {
        this.projectIds = [];
      }
    },
    'currentFilter.properties.portfolio_id': function (portfolioIds) {
      if (portfolioIds?.length) {
        const multiviewData = multiViewsProjects.getProjectDataById(portfolioIds[0]);
        const multivewProjectIds = multiviewData?.ganttIDs ? [...multiviewData.ganttIDs] : [];

        this.multiviewProjectIds = multivewProjectIds;
      } else {
        this.multiviewProjectIds = [];
      }
    },
    currentProjectIds(ids) {
      this.init();
      this.isShowSkeleton = false;
    },
    projectsByStatusesData(data) {
      if (data && data.items.length) {
        this.isStub = false;
      } else {
        setTimeout(() => {
          this.isStub = true;
        }, 0);
      }
    },
  },
  mounted() {
    if (this.currentFilter?.properties?.gantt_id?.length) {
      this.projectIds = [...this.currentFilter.properties.gantt_id];
    }

    if (this.currentFilter?.properties?.portfolio_id?.length) {
      const multiviewData = multiViewsProjects.getProjectDataById(this.currentFilter?.properties?.portfolio_id[0]);
      const multivewProjectIds = multiviewData?.ganttIDs ? [...multiviewData.ganttIDs] : [];

      this.multiviewProjectIds = multivewProjectIds;
    }

    const projectIds = [this.projectIds, this.multiviewProjectIds].find(arr => arr.length) || [];

    if (!projectIds.length) {
      this.projectIds = projectsModel.getAllProjects().map(project => project.gantt_id);
    }

    const reportNameAnalytics = this.pageTitle.replace(/^exmp_chart_/, '').replace(/_title$/, '');

    userExtAnalytics.log('report_open', {
      report_type: reportNameAnalytics,
      source_type: this.sourceType,
      projects_selected_count: this.projectIds.length || this.multiviewProjectIds.length,
    });
    this.calcContentHeight();
    window.addEventListener('resize', this.calcContentHeight);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.calcContentHeight);
  },
  methods: {
    calcContentHeight() {
      const viewportHeight = document.documentElement.clientHeight || window.innerHeight || 0;

      const titleRef = this.$refs.reportsTitle;
      const toolbarRef = this.$refs.reportsToolbar;

      const titleEl = titleRef && (titleRef.$el || titleRef);
      const toolbarEl = toolbarRef && (toolbarRef.$el || toolbarRef);

      const titleHeight = titleEl?.getBoundingClientRect().height || 0;
      const toolbarHeight = toolbarEl?.getBoundingClientRect().height || 0;

      const height = Math.max(viewportHeight - titleHeight - toolbarHeight - 90, 0);

      this.contentHeight = `${height}px`;
    },
    init() {
      const data = {
        items: [],
      };

      if (!this.currentProjectIds.length) {
        return data;
      }

      const colors = this.$store.getters['teamSetting/colors'].data;
      const projectsStatuses = this.currentProjectIds.map(ganttID => {
        const statusId = this.$store.getters['teamSetting/statusDataForProject'](ganttID).selectOption.id;

        return [ganttID, statusId];
      });

      const projectStatusesLocales = ['no_status_locale', 'on_track_locale', 'at_risk_locale', 'off_track_locale'];

      data.items = this.$store.getters['teamSetting/projectStatuses']
        .find(item => item.name === 'status')
        ?.options.map(status => {
          const qty = projectsStatuses.filter(([projectId, statusId]) => statusId === status.id).length;
          const value = Math.round((qty / projectsStatuses.length) * 100);

          return {
            id: status.id,
            color: colors.find(color => color.id === status.color).hex2,
            name: projectStatusesLocales.includes(status.value) ? __(status.value) : status.value,
            qty,
            value,
          };
        });

      this.projectsByStatusesData = data;
    },
    toggleLegend(flag) {
      this.disabledLegends = !flag;
    },

    onExport() {
      Export.exportReportGraphicToPdf({
        title: __(`${this.pageTitle}`),
      });

      userExtAnalytics.log('report_export_done', {
        export_format: 'PDF',
        report_type: 'report_statuses',
      });
    },
  },
};
</script>

<style module src='./../less/reportsMain.less'></style>
